import { useState, useEffect } from "react";
import { ratingsApi } from "@/lib/api";

interface RatingData {
  average: number;
  count: number;
}

// Global cache for ratings to prevent repeated loading
const ratingCache: Record<string, RatingData> = {};

export function useServiceRating(creatorId?: string, serviceId?: string) {
  const cacheKey = `${creatorId}:${serviceId}`;
  const [rating, setRating] = useState<RatingData | null>(ratingCache[cacheKey] || null);
  const [loading, setLoading] = useState(!ratingCache[cacheKey]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRating = async () => {
      if (!creatorId || !serviceId) {
        setRating(null);
        setLoading(false);
        return;
      }

      // If we already have a value in state from the initial render (via cache), 
      // check if we still need to load. If it's already in cache, skip loading if desired.
      // However, we still might want to refresh in background. 
      // For this task, we want to AVOID the loading indicator flicker.
      
      try {
        if (!ratingCache[cacheKey]) {
          setLoading(true);
        }
        setError(null);

        // Fetch all reviews for the creator
        const response = await ratingsApi.getByUserId(creatorId) as { ratings: any[] };

        if (response?.ratings && Array.isArray(response.ratings)) {
          // Filter reviews for this specific service
          const filteredReviews = response.ratings.filter((review: any) => {
            const reviewServiceId = review.serviceId || review.order?.serviceId;
            return reviewServiceId === serviceId;
          });

          // Calculate rating summary and purchase count
          let newRating: RatingData;
          if (filteredReviews.length > 0) {
            const total = filteredReviews.reduce((sum, r) => sum + r.rating, 0);
            const average = total / filteredReviews.length;
            newRating = {
              average: Math.round(average * 10) / 10,
              count: filteredReviews.length,
            };
          } else {
            newRating = { average: 0, count: 0 };
          }
          
          setRating(newRating);
          ratingCache[cacheKey] = newRating;
        }
      } catch (err) {
        console.error("Failed to fetch ratings:", err);
        setError("Failed to load ratings");
        setRating(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRating();
  }, [creatorId, serviceId, cacheKey]);

  return { rating, loading, error };
}
